import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { COUNTIES, PROPERTY_TYPES, CURRENCIES } from "@/data/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, X, Building2, MapPin, PlusCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { InstallPrompt } from "@/components/InstallPrompt";
import { SaveSearchDialog } from "@/components/SaveSearchDialog";

const HERO =
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SHORT_STAY_FILTERS = ["WiFi", "Free parking", "Self check-in", "Air conditioning", "Swimming pool"];

const EMPTY = {
  q: "",
  county: "",
  town: "",
  property_type: "",
  listing_type: "",
  currency: "",
  min_price: "",
  max_price: "",
  nights: "",
  st_addons: [],
};

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => {
        if (k === "st_addons") return;
        if (v !== "" && v != null) params[k] = v;
      });
      if (f.st_addons?.length) params.addons = f.st_addons.join(",");
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const setField = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const toggleAddon = (a) =>
    setFilters((p) => ({ ...p, st_addons: p.st_addons.includes(a) ? p.st_addons.filter((x) => x !== a) : [...p.st_addons, a] }));
  const apply = () => setApplied(filters);
  const clear = () => {
    setFilters(EMPTY);
    setApplied(EMPTY);
  };

  const activeCount = Object.entries(applied).filter(([k, v]) => (k === "st_addons" ? v.length > 0 : v !== "")).length;

  return (
    <div>
      <InstallPrompt />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#2C3830]/75" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-14 sm:py-20 lg:px-8 lg:py-28">
          <span className="label-caps text-[#D4A373]">Rentals · Sales · Short Stays · Kenya</span>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Find the space that fits your life.
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/75 sm:mt-5 sm:text-base">
            Browse verified listings from owners and agents — long-term rentals, homes for sale,
            student housing and short-term stays. Filter, then unlock direct contact.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              data-testid="list-your-property-cta"
              onClick={() => navigate(user ? "/create" : "/register?next=/create")}
              className="rounded-full bg-[#D4A373] px-6 py-6 text-base font-medium text-[#2C3830] hover:bg-[#c8925f] sm:py-5"
            >
              <PlusCircle className="mr-2 h-5 w-5" /> List your property
            </Button>
            <span className="text-sm text-white/70">
              Have a place to rent, sell or host? Post it in minutes.
            </span>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-[73px] z-40 border-b border-border/60 bg-[#F7F5F0]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-4 lg:px-8">
          {/* Mobile: search + collapsible toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
              <Input
                data-testid="filter-search-mobile"
                value={filters.q}
                onChange={(e) => setField("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="Search…"
                className="rounded-full bg-white pl-9"
              />
            </div>
            <Button
              data-testid="filter-toggle"
              onClick={() => setOpen((o) => !o)}
              variant="outline"
              className="shrink-0 rounded-full border-[#2C3830]/20 bg-white text-[#2C3830]"
            >
              <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
              {activeCount > 0 && (
                <span className="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2C3830] px-1 text-xs text-white">
                  {activeCount}
                </span>
              )}
            </Button>
          </div>

          {/* Filter body — collapsible on mobile, always visible md+ */}
          <div className={`${open ? "mt-3 block" : "hidden"} md:mt-0 md:block`}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
            <div className="relative col-span-2 hidden md:block lg:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
              <Input
                data-testid="filter-search"
                value={filters.q}
                onChange={(e) => setField("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="Search estate, title…"
                className="rounded-full bg-white pl-9"
              />
            </div>

            <Select value={filters.county || "any"} onValueChange={(v) => setField("county", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-county" className="rounded-full bg-white">
                <SelectValue placeholder="County" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All counties</SelectItem>
                {COUNTIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.property_type || "any"} onValueChange={(v) => setField("property_type", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-type" className="rounded-full bg-white">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All types</SelectItem>
                {PROPERTY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.listing_type || "any"} onValueChange={(v) => setField("listing_type", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-listing-type" className="rounded-full bg-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">All categories</SelectItem>
                <SelectItem value="rent">For Rent</SelectItem>
                <SelectItem value="sale">For Sale</SelectItem>
                <SelectItem value="short_term">Short-term Stay</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.currency || "any"} onValueChange={(v) => setField("currency", v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-currency" className="rounded-full bg-white">
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any currency</SelectItem>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 lg:flex lg:items-center">
            <Input
              data-testid="filter-town"
              value={filters.town}
              onChange={(e) => setField("town", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              placeholder="Town / area"
              className="rounded-full bg-white lg:w-44"
            />
            <Input
              data-testid="filter-min-price"
              type="number"
              value={filters.min_price}
              onChange={(e) => setField("min_price", e.target.value)}
              placeholder="Min price"
              className="rounded-full bg-white lg:w-32"
            />
            <Input
              data-testid="filter-max-price"
              type="number"
              value={filters.max_price}
              onChange={(e) => setField("max_price", e.target.value)}
              placeholder="Max price"
              className="rounded-full bg-white lg:w-32"
            />
            <div className="col-span-2 flex flex-wrap gap-2 md:col-span-4 lg:ml-auto lg:col-span-1">
              <Button
                data-testid="filter-apply"
                onClick={apply}
                className="flex-1 rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40] lg:flex-none"
              >
                <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Apply
              </Button>
              <SaveSearchDialog
                source="home"
                params={{
                  q: filters.q, county: filters.county, town: filters.town,
                  property_type: filters.property_type, listing_type: filters.listing_type,
                  currency: filters.currency,
                  min_price: filters.min_price ? Number(filters.min_price) : "",
                  max_price: filters.max_price ? Number(filters.max_price) : "",
                  nights: filters.nights ? Number(filters.nights) : "",
                  addons: filters.st_addons,
                }}
              />
              {activeCount > 0 && (
                <Button
                  data-testid="filter-clear"
                  onClick={clear}
                  variant="ghost"
                  className="rounded-full text-[#6B7280]"
                >
                  <X className="mr-1 h-4 w-4" /> Clear
                </Button>
              )}
            </div>
          </div>
          {filters.listing_type === "short_term" && (
            <div data-testid="short-stay-filters" className="mt-3 border-t border-border/60 pt-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  data-testid="filter-nights"
                  type="number"
                  min="1"
                  value={filters.nights}
                  onChange={(e) => setField("nights", e.target.value)}
                  placeholder="Nights"
                  className="rounded-full bg-white lg:w-28"
                />
                <span className="text-xs text-[#6B7280]">Amenities:</span>
                {SHORT_STAY_FILTERS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`filter-addon-${a}`}
                    onClick={() => toggleAddon(a)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      filters.st_addons.includes(a) ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border bg-white text-[#6B7280] hover:border-[#2C3830]"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="mb-6 flex items-center gap-2 text-[#6B7280]">
          <MapPin className="h-4 w-4 text-[#D4A373]" />
          <span data-testid="results-count" className="text-sm">
            {loading ? "Loading…" : `${listings.length} propert${listings.length === 1 ? "y" : "ies"} found`}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-80 rounded-2xl" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div data-testid="empty-state" className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-24 text-center">
            <Building2 className="h-10 w-10 text-[#D4A373]" />
            <p className="font-display text-xl text-[#2C3830]">No properties match your search</p>
            <p className="text-sm text-[#6B7280]">Try widening your filters or clearing them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {listings.map((l, i) => (
              <PropertyCard key={l.id} listing={l} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
